import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Button from 'Components/UI/Button';
import moment from 'moment-timezone';
import { useHistory } from 'react-router-dom';
import SkeletonOverlay from 'Components/UI/SkeletonOverlay';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import {
  closeSchedulerModal, fetchDataFromLinkQueryParam,
  setNewLinkQueryParam, useAgentSchedulerDispatchContext,
  useAgentSchedulerStateContext,
} from 'Context/NewAgentSchedulerContext';
import fr from 'facet-rest';
import { removeQuery } from 'Helpers/Query';
import { useMissionControlURL } from 'Hooks/useMissionControlURL';

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    height: '33%',
  },
  dateHeaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    background: theme.palette.facet.accentBlue15,
    color: theme.palette.facet.accentBlue4,
    padding: theme.spacing(4, 8, 4, 8),
  },
  dateHeader: {
    fontSize: '1rem',
  },
  date: {
    fontSize: '1.5rem',
    margin: theme.spacing(1),
  },
  button: {
    borderRadius: theme.radius.md,
    color: theme.palette.common.white,
    height: '100%',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  scheduleButton: {
    background: theme.palette.facet.accentGreen,
  },
  scheduleButtonTitle: {
    fontSize: '1.2rem',
  },
  scheduleButtonBody: {},
  returnToMCButton: {
    background: theme.palette.facet.accentBlue2,
    fontSize: '1.2rem',
    overflowWrap: 'break-word',
  },
});

const latestMeetingDateFormat = 'dddd, MMMM Do [at] h:mmA z';
const projectedDateFormat = 'MMMM Do';

const NextMeeting = ({ classes, className }) => {
  const { householdResource } = useSimpleHousehold();
  const history = useHistory();
  const { currentCollection, projectedMeetingCollection, loading } = useAgentSchedulerStateContext();
  const dispatch = useAgentSchedulerDispatchContext();
  const nextScheduledMeeting = currentCollection.resources().head();
  const nextProjectedMeeting = projectedMeetingCollection?.resources().head();
  const householdTimezone = householdResource.attr('timeZone');
  const missionControlURL = useMissionControlURL();

  const navigateToMissionControl = () => {
    closeSchedulerModal();
    history.push(missionControlURL());
  };

  const goToMonthlyView = () => {
    removeQuery('meetingBooked');
    setNewLinkQueryParam(dispatch, nextProjectedMeeting.relationship(fr.relationships.projectedMeetingAvailabilityPeriod.name).link('related').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.dateHeaderContainer}>
        <div className={classes.dateHeader}>
            Your meeting has been scheduled for:
        </div>
        <div className={classes.date}>
          {moment(nextScheduledMeeting.attr('startTime')).tz(moment.tz.guess()).format(latestMeetingDateFormat)}
        </div>
        {householdTimezone && (
          <div className={classes.date}>
            Client Time: {moment(nextScheduledMeeting.attr('startTime')).tz(householdTimezone).format('h:mmA z')}
          </div>
        )}
      </div>
      <div className={classes.buttonContainer}>
        {nextProjectedMeeting && (
          <SkeletonOverlay loading={!loading}>
            <Button
              className={classNames(classes.button, classes.scheduleButton)}
              onClick={goToMonthlyView}
            >
              <div className={classes.scheduleButtonBody}>
                <div className={classes.scheduleButtonTitle}>Schedule New Mtg.</div>
                <div>Projected Week of {moment(nextProjectedMeeting?.attr('start')).format(projectedDateFormat)}</div>
              </div>
            </Button>
          </SkeletonOverlay>
        )}
        <Button
          className={classNames(classes.button, classes.returnToMCButton)}
          onClick={navigateToMissionControl}
        >
          <div>Return to Mission Control</div>
        </Button>
      </div>
    </div>
  );
};

NextMeeting.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

NextMeeting.defaultProps = {
  className: '',
};

export default withStyles(styles)(NextMeeting);
